import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ITEMS_PER_PAGE = 12;

const JourneyOverviewReport = ({ token }) => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('updated');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadReport();
  }, []);

  const loadReport = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        'http://localhost:5000/api/journeys/report/overview',
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setReportData(response.data.report);
      setCurrentPage(1);
    } catch (err) {
      setErrorMessage('Failed to load report');
      console.error('Report error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredAndSortedJourneys = () => {
    if (!reportData || !reportData.journeys) return { filtered: [], paginatedResults: [], totalPages: 0, totalCount: 0 };

    let filtered = [...reportData.journeys];

    // Filter by search term (title match)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(journey =>
        journey.title.toLowerCase().includes(term)
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(journey => journey.status === statusFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.title.localeCompare(b.title);
        case 'updated':
          return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
        case 'created':
          return new Date(b.created_at) - new Date(a.created_at);
        default:
          return 0;
      }
    });

    // Calculate pagination
    const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginatedResults = filtered.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    return {
      filtered,
      paginatedResults,
      totalPages,
      totalCount: filtered.length,
    };
  };

  const exportToCSV = () => {
    if (!reportData) return;

    const headers = [
      'Title',
      'Status',
      'Stage',
      'Facilitator',
      'Team Members',
      'Ideas',
      'Hypotheses',
      'Experiments',
      'Decisions',
      'Created',
      'Last Updated',
    ];

    const rows = reportData.journeys.map(j => [
      j.title,
      j.status,
      j.current_stage,
      j.facilitator,
      j.team_count,
      j.ideas_count,
      j.hypotheses_count,
      j.experiments_count,
      j.decisions_count,
      new Date(j.created_at).toLocaleDateString(),
      new Date(j.updated_at).toLocaleDateString(),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `journey-overview-report-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="form-card">
        <h2 className="form-title">📊 Journey Overview Report</h2>
        <p style={{ textAlign: 'center', color: '#999' }}>Loading report...</p>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="form-card">
        <h2 className="form-title">📊 Journey Overview Report</h2>
        <div className="form-error">{errorMessage}</div>
      </div>
    );
  }

  return (
    <div className="form-card">
      <h2 className="form-title">📊 Journey Overview Report</h2>

      {/* Report Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div>
          <p style={{ margin: '0', color: '#666' }}>
            Generated: {new Date(reportData.generated_at).toLocaleString()}
          </p>
          <p style={{ margin: '5px 0 0 0', fontSize: '18px', fontWeight: 'bold', color: '#0066cc' }}>
            Total Journeys: {reportData.total_journeys}
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={exportToCSV}
          style={{ padding: '10px 20px' }}
        >
          📥 Export to CSV
        </button>
      </div>

      {/* Search, Filter, Sort Bar */}
      {reportData.total_journeys > 0 && (
        <div className="search-filter-bar" style={{ marginBottom: '20px' }}>
          <div className="search-input-wrapper">
            <input
              type="text"
              placeholder="Search journeys by title..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </div>

          {/* Filter Pills */}
          <div className="filter-pills" style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '12px' }}>
            <label style={{ fontWeight: '500', color: '#333' }}>Status:</label>
            {['all', 'active', 'paused'].map(status => (
              <button
                key={status}
                onClick={() => {
                  setStatusFilter(status);
                  setCurrentPage(1);
                }}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #dee2e6',
                  borderRadius: '20px',
                  backgroundColor: statusFilter === status ? '#0066cc' : 'white',
                  color: statusFilter === status ? 'white' : '#333',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '13px',
                  transition: 'all 0.2s',
                }}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>

          {/* Sort and Results Counter */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontWeight: '500', color: '#333' }}>Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  fontSize: '13px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                }}
              >
                <option value="updated">Recently Updated</option>
                <option value="created">Recently Created</option>
                <option value="name">Name (A-Z)</option>
              </select>
            </div>
            <div style={{ color: '#666', fontSize: '13px' }}>
              {(() => {
                const { totalCount, paginatedResults } = getFilteredAndSortedJourneys();
                return `Showing ${paginatedResults.length > 0 ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0} - ${(currentPage - 1) * ITEMS_PER_PAGE + paginatedResults.length} of ${totalCount}`;
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Report Table */}
      {reportData.total_journeys === 0 ? (
        <p style={{ textAlign: 'center', color: '#999' }}>No journeys found</p>
      ) : (
        <>
          {(() => {
            const { paginatedResults, totalPages, totalCount } = getFilteredAndSortedJourneys();
            return (
              <>
                {totalCount === 0 ? (
                  <p style={{ textAlign: 'center', color: '#999' }}>No journeys match your search criteria</p>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '13px',
                      }}
                    >
                      <thead>
                        <tr style={{ backgroundColor: '#f0f6ff', borderBottom: '2px solid #0066cc' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#0066cc' }}>
                  Title
                </th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#0066cc' }}>
                  Status
                </th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#0066cc' }}>
                  Stage
                </th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#0066cc' }}>
                  Facilitator
                </th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#0066cc' }}>
                  Team
                </th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#0066cc' }}>
                  Ideas
                </th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#0066cc' }}>
                  Hypotheses
                </th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#0066cc' }}>
                  Experiments
                </th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#0066cc' }}>
                  Decisions
                </th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#0066cc' }}>
                  Created
                </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedResults.map((journey, idx) => (
                <tr
                  key={journey.id}
                  style={{
                    borderBottom: '1px solid #e0e0e0',
                    backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f9f9f9',
                  }}
                >
                  <td style={{ padding: '12px', color: '#333' }}>
                    <strong>{journey.title}</strong>
                  </td>
                  <td style={{ padding: '12px', color: '#666' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor:
                          journey.status === 'active'
                            ? '#d4edda'
                            : journey.status === 'paused'
                            ? '#fff3cd'
                            : '#d1ecf1',
                        color:
                          journey.status === 'active'
                            ? '#155724'
                            : journey.status === 'paused'
                            ? '#856404'
                            : '#0c5460',
                        fontSize: '12px',
                        fontWeight: '600',
                      }}
                    >
                      {journey.status.charAt(0).toUpperCase() + journey.status.slice(1)}
                    </span>
                  </td>
                  <td style={{ padding: '12px', color: '#666' }}>
                    {journey.current_stage.charAt(0).toUpperCase() + journey.current_stage.slice(1)}
                  </td>
                  <td style={{ padding: '12px', color: '#666' }}>{journey.facilitator}</td>
                  <td style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#0066cc' }}>
                    {journey.team_count}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#0066cc' }}>
                    {journey.ideas_count}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#0066cc' }}>
                    {journey.hypotheses_count}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#0066cc' }}>
                    {journey.experiments_count}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#0066cc' }}>
                    {journey.decisions_count}
                  </td>
                  <td style={{ padding: '12px', color: '#666' }}>
                    {new Date(journey.created_at).toLocaleDateString()}
                  </td>
                    </tr>
                      ))}
                    </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '20px' }}>
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      style={{
                        padding: '8px 12px',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        backgroundColor: currentPage === 1 ? '#f5f5f5' : 'white',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        opacity: currentPage === 1 ? 0.6 : 1,
                      }}
                    >
                      ← Previous
                    </button>
                    <div style={{ color: '#666' }}>
                      Page {currentPage} of {totalPages}
                    </div>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      style={{
                        padding: '8px 12px',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        backgroundColor: currentPage === totalPages ? '#f5f5f5' : 'white',
                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                        opacity: currentPage === totalPages ? 0.6 : 1,
                      }}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            );
          })()}
        </>
      )}

      {/* Summary Stats */}
      {(() => {
        const { filtered } = getFilteredAndSortedJourneys();
        return (
          <div
            style={{
              marginTop: '30px',
              paddingTop: '20px',
              borderTop: '1px solid #e0e0e0',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '20px',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <p style={{ margin: '0 0 5px 0', fontSize: '12px', color: '#7f8c8d', textTransform: 'uppercase' }}>
                Total Ideas
              </p>
              <p style={{ margin: '0', fontSize: '24px', fontWeight: 'bold', color: '#0066cc' }}>
                {filtered.reduce((sum, j) => sum + j.ideas_count, 0)}
              </p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ margin: '0 0 5px 0', fontSize: '12px', color: '#7f8c8d', textTransform: 'uppercase' }}>
                Total Hypotheses
              </p>
              <p style={{ margin: '0', fontSize: '24px', fontWeight: 'bold', color: '#0066cc' }}>
                {filtered.reduce((sum, j) => sum + j.hypotheses_count, 0)}
              </p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ margin: '0 0 5px 0', fontSize: '12px', color: '#7f8c8d', textTransform: 'uppercase' }}>
                Total Experiments
              </p>
              <p style={{ margin: '0', fontSize: '24px', fontWeight: 'bold', color: '#0066cc' }}>
                {filtered.reduce((sum, j) => sum + j.experiments_count, 0)}
              </p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ margin: '0 0 5px 0', fontSize: '12px', color: '#7f8c8d', textTransform: 'uppercase' }}>
                Total Decisions
              </p>
              <p style={{ margin: '0', fontSize: '24px', fontWeight: 'bold', color: '#0066cc' }}>
                {filtered.reduce((sum, j) => sum + j.decisions_count, 0)}
              </p>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default JourneyOverviewReport;
